import React from 'react';
import type { LogoPosition } from '../../types/forms';

interface FormLogoProps {
  logoUrl?: string;
  logoPosition?: LogoPosition;
  logoMargin?: number;
  /** Escala do logo em percentual (50–200, default 100). Story saas-1.7. */
  logoScale?: number;
  /**
   * 'absolute' renderiza as posições top-* posicionadas (top × left/center/right) e bottom-*
   *   em telas SEM footer fixo (Welcome/ThankYou/Redirect/FormPublic classic).
   * 'inline' renderiza apenas quando position === 'middle-center', no fluxo acima da pergunta.
   * 'footer-inline' (saas-1.24.A): renderiza bottom-* DENTRO do <footer> da Question Screen,
   *   após OneFormBadge — mandato user 2026-05-04 (logo SEMPRE abaixo do botão de confirmar).
   *   Mapeia bottom-left/center/right → justify-start/center/end no flex do footer.
   *   Consumidores colocam ambos: 'absolute' no container raiz (top-*) e 'footer-inline' no <footer> (bottom-*).
   * 'static' (mobile-ui.6.C C2): renderiza no fluxo para QUALQUER posição exceto middle-center
   *   (que já renderiza via 'inline'). Consumidor controla visibilidade (ex.: welcome short —
   *   logo abaixo do CTA, wrapper hidden short:block; instância absolute ganha short:hidden).
   */
  variant: 'absolute' | 'inline' | 'footer-inline' | 'static';
  /** Tailwind z-index helper; default z-[15] (entre bg z-0/z-10 e header/footer z-20). saas-1.23.A. */
  zIndexClass?: string;
  /**
   * (saas-1.24.A.1 hotfix) Quando true, o variant="absolute" NÃO renderiza bottom-* —
   * delega para uma instância paralela de variant="footer-inline" no mesmo container.
   * Question Screen passa true (tem footer-inline); Welcome/ThankYou/Redirect/FormPublic NÃO passam (default false).
   * Previne duplicação de logo quando ambas variants existem na mesma tela.
   */
  excludeAbsoluteBottom?: boolean;
}

const LOGO_BASE_HEIGHT_PX = 32; // h-8 — baseline anterior à saas-1.7

export const FormLogo: React.FC<FormLogoProps> = ({
  logoUrl,
  logoPosition = 'top-left',
  logoMargin = 16,
  logoScale = 100,
  variant,
  zIndexClass = 'z-[15]',
  excludeAbsoluteBottom = false,
}) => {
  if (!logoUrl) return null;

  const isMiddleCenter = logoPosition === 'middle-center';
  const height = LOGO_BASE_HEIGHT_PX * (logoScale / 100);
  const imgStyle: React.CSSProperties = { height: `${height}px` };

  if (variant === 'inline') {
    if (!isMiddleCenter) return null;
    // logoMargin aplica como marginBottom (espaçamento vertical entre logo e pergunta)
    return (
      <div className="flex justify-center" style={{ marginBottom: `${logoMargin}px` }}>
        <img src={logoUrl} alt="Logo" className="w-auto object-contain" style={imgStyle} />
      </div>
    );
  }

  if (variant === 'static') {
    // middle-center delega ao variant='inline' (evita duplicação na mesma tela)
    if (isMiddleCenter) return null;
    return (
      <div className="flex justify-center mt-4">
        <img src={logoUrl} alt="Logo" className="w-auto object-contain short:max-h-5" style={imgStyle} />
      </div>
    );
  }

  if (variant === 'footer-inline') {
    // Só renderiza para bottom-* (top-* permanece via variant="absolute")
    if (!logoPosition.startsWith('bottom')) return null;
    if (isMiddleCenter) return null;

    // Map bottom-left/center/right → justify-start/center/end (alinhamento horizontal no footer flex)
    const justify = logoPosition.endsWith('left') ? 'justify-start'
                  : logoPosition.endsWith('right') ? 'justify-end'
                  : 'justify-center';

    // mt-3 (12px) cria separação visual do OneFormBadge sem desconectar (Uma Decisão UX 2 ajuste 3).
    // logoMargin user NÃO se aplica em footer-inline (era para distância da edge; agora é distância do badge).
    // mobile-ui.6.C C1b: logo clampado a 20px no short (max-h vence o height inline; w-auto
    // preserva proporção). NÃO esconder (branding fail-closed).
    // mobile-ui.6.E E3: o short:mt-1 da 6.C espremia o bloco logo+badge contra o botão OK
    // ("tá muito espremido" — PO smoke round 4); override removido — mt-3 (12px) vale
    // também no short, sem crescer o logo (short:max-h-5 mantido).
    return (
      <div className={`flex ${justify} w-full mt-3`}>
        <img src={logoUrl} alt="Logo" className="w-auto object-contain short:max-h-5" style={imgStyle} />
      </div>
    );
  }

  // variant === 'absolute' — only renders for the 6 non-middle positions
  if (isMiddleCenter) return null;
  // saas-1.24.A.1 hotfix: skip bottom-* quando container tem footer-inline paralelo (anti-duplicação)
  if (excludeAbsoluteBottom && logoPosition.startsWith('bottom')) return null;

  const wrapperStyle: React.CSSProperties = {};
  const m = `${logoMargin}px`;

  if (logoPosition.startsWith('top')) {
    wrapperStyle.top = m;
  } else {
    wrapperStyle.bottom = m;
  }

  if (logoPosition.endsWith('left')) wrapperStyle.left = m;
  else if (logoPosition.endsWith('right')) wrapperStyle.right = m;
  else {
    // top-center | bottom-center
    wrapperStyle.left = '50%';
    wrapperStyle.transform = 'translateX(-50%)';
  }

  return (
    <div className={`absolute ${zIndexClass} pointer-events-none`} style={wrapperStyle}>
      <img src={logoUrl} alt="Logo" className="w-auto object-contain" style={imgStyle} />
    </div>
  );
};
